import { find } from 'lodash-es';
import { GA4CheckoutEventData, Item } from '../../@types/window-extended';
import { ga4options } from '../templates/product-listing-page/quick-add/QuickAddPanel';
import { ProductNode } from '../templates/product-listing-page/types';
import { IColorwayProductData, IProduct, IVariant } from '../templates/product/types/PDP';
import { AddedToCartProduct } from '../templates/_shared/ui-components/ATCModal/types';
import { CartItem } from './cart';
import { globalWindow } from './constants';
import { getCookie, setCookie } from './cookies';
import { IS_NATIVE_APP_CLIENT, retryPromise } from './helpers';
import { getIdFromShopifyGraphQLId } from './shopify';
import { Customer, InitialUrlSearchParams, isProduction } from './environment';
import { LatLng } from './findInStores';
import { WishlistGA4Options, WishlistGA4ProductData } from '../templates/_shared/wishlist/wishlistState';
import { urlWithoutLocale } from './localization';

const checkIfGTMisReadyPromise = () => {
  return new Promise<boolean>(function (resolve, reject) {
    if (
      window.dataLayer &&
      typeof window.dataLayer.push === 'function' &&
      window.dataLayer.push !== Array.prototype.push
    ) {
      resolve(true);
    } else {
      reject("GTM isn't ready yet.");
    }
  });
};

// analytics helper functions
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function pushEventToGTMDataLayer(dataLayerObject: Record<string, any>) {
  const isGTMready = await retryPromise(checkIfGTMisReadyPromise, 10, { delay: 1000 });

  if (isGTMready && dataLayerObject?.event) {
    globalWindow.dataLayer.push(dataLayerObject);
  } else if (typeof dataLayerObject?.eventCallback === 'function') {
    // Need the call the eventCallback if GTM doesn't exist to call it.
    dataLayerObject.eventCallback();
  }
}

export interface IPdpInfo {
  sku: string;
  // ? [REVISIT] upcapitalize & deprecate these fields in favor of consistent cases; don't assume color & size
  Color: string;
  Size: string;
  availableForSale: boolean;
  handle: string;
  title: string;
  price: number;
  strikethroughPrice: number;
  productType: string;
  imageUrl: string;
  quantity?: number;

  // ? [REVISIT] handle & url is redundant
  url: string;
  displayPrice: number;
}

export const pushPdpInfoToDatalayer = (pdpInfo: IPdpInfo) => {
  pushEventToGTMDataLayer({
    event: 'alo_PDP_pageView',
    pdpInfo: pdpInfo,
  });
};

// ? [REVISIT] Ideally I would like to delete several of these functions since they are not used in the codebase.
export const processSelectedVariantsForGTMUpdates = (
  productData: IProduct,
  variantData: IVariant,
  colorProductData: IColorwayProductData
): IPdpInfo => {
  const { productType } = productData;
  const { handle, title } = colorProductData;

  const strikethroughPrice = variantData.displayPrice || variantData.price;
  return {
    sku: variantData.sku,
    Color: variantData.selectionOptions.Color,
    Size: variantData.selectionOptions.Size,
    availableForSale: variantData.availableForSale,
    handle: handle,
    price: variantData.price,
    strikethroughPrice,
    productType: productType,
    title: title,
    imageUrl: variantData.imageUrl,
    url: `https://www.aloyoga.com/products/${handle}`,

    // deprecated field
    displayPrice: strikethroughPrice,
  };
};

export const processCartItemForGTMUpdates = (cartItem: CartItem): IPdpInfo => {
  const color = find(cartItem.options_with_values, { name: 'Color' })?.value;
  const size = find(cartItem.options_with_values, { name: 'Size' })?.value;
  const imageUrl = cartItem.image;

  const strikethroughPrice = cartItem.displayPrice ?? cartItem.price;
  return {
    sku: cartItem.sku,
    Color: color ?? 'nocolor',
    Size: size ?? 'nosize',
    availableForSale: true,
    handle: cartItem.handle,
    price: cartItem.price,
    strikethroughPrice,
    productType: cartItem.product_type,
    title: cartItem.title,
    imageUrl: imageUrl,
    url: `https://www.aloyoga.com${cartItem.url}`,
    quantity: cartItem.quantity,

    // deprecated field
    displayPrice: strikethroughPrice,
  };
};

export interface ICartInfo {
  sku: string;
  price: number;
  displayPrice: number;
  description: string;
  productType: string;
  handle: string;
  title: string;
  Color: string;
  Size: string;
  imageUrl: string;
}
export const processCartForGTMTrackCart = (cartData: CartItem[]): ICartInfo[] => {
  return cartData.map((regularItem) => {
    const color = find(regularItem.options_with_values, { name: 'Color' })?.value ?? 'nocolor';
    const size = find(regularItem.options_with_values, { name: 'Size' })?.value ?? 'nocolor';
    return {
      sku: regularItem.sku,
      price: regularItem.price,
      displayPrice: regularItem.displayPrice ?? regularItem.price,
      description: regularItem.product_description,
      productType: regularItem.product_type,
      Color: color,
      Size: size,
      handle: regularItem.handle,
      title: regularItem.title,
      imageUrl: regularItem.image,
      quantity: regularItem.quantity,
    };
  });
};

export const pushCartInfoToDataLayer = (cartInfoArray: ICartInfo[]) => {
  cartInfoArray.forEach((cartInfo) => {
    pushEventToGTMDataLayer({
      event: 'alo_currentCartState',
      currentCartState: cartInfo,
    });
  });
};

// GA4
export const clearPrevEcommerceObject = () => {
  window.dataLayer.push({ ecommerce: null });
};

export const splitProductType = (productType: string) => productType.split(':');
interface fnOptions {
  isDecrement?: boolean;
  setTitle?: string;
  isRemoving?: boolean;
  isCart?: boolean;
  ga4options?: ga4options;
}

type ProductInputForGA4 = Omit<AddedToCartProduct, 'properties'>;

export const fireGA4ecommerceAddOrRemoveProductEvent = (products: ProductInputForGA4[], options: fnOptions) => {
  const {
    isDecrement = false,
    setTitle = '',
    isRemoving = false,
    isCart = false,
    ga4options = { index: '', collectionHandle: '', collectionShopifyId: '' },
  } = options;
  clearPrevEcommerceObject();
  const event = isDecrement || isRemoving ? 'remove_from_cart' : 'add_to_cart';
  const items = products.map((product, index) => {
    const colorName = product.options_with_values.find((option) => option.name === 'Color')?.value;
    const productTitle = colorName && isCart ? `${product.product_title} - ${colorName}` : product.product_title;
    const productTypeBreakdown = splitProductType(product.product_type);
    return {
      item_name: productTitle,
      item_id: product.product_id.toString(),
      affiliation: 'Alo_US',
      item_brand: 'alo',
      item_category: productTypeBreakdown[0] || '',
      item_category2: productTypeBreakdown[1] || '',
      item_category3: productTypeBreakdown[2] || '',
      item_category4: productTypeBreakdown[3] || '',
      item_variant: product.variant_title,
      item_list_name: setTitle || ga4options.collectionHandle, // If associated with a set or quick add
      item_list_id: ga4options.collectionShopifyId,
      index: setTitle?.length > 0 ? index + 1 : ga4options.index, // If associated with a set or quick add
      quantity: isRemoving ? product.quantity : 1,
    };
  });

  pushEventToGTMDataLayer({
    event,
    is_a_set: items.length > 1,
    ecommerce: {
      items,
    },
    ...(setTitle.length > 1 && { set_title: setTitle }),
  });
};

export const fireGA4viewProductEvent = (products: IColorwayProductData[], setTitle?: string) => {
  clearPrevEcommerceObject();
  const items = products.map((product, index) => {
    const productTypeBreakdown = splitProductType(product.productType);
    return {
      item_name: product.title,
      item_id: product.id,
      item_brand: 'alo',
      affiliation: 'Alo_US',
      item_category: productTypeBreakdown[0] || '',
      item_category2: productTypeBreakdown[1] || '',
      item_category3: productTypeBreakdown[2] || '',
      item_category4: productTypeBreakdown[3] || '',
      item_variant: `${product.variants[0]?.selectionOptions.Color}`,
      item_list_name: setTitle || '', // If associated with a set.
      index: setTitle ? index + 1 : '', // If associated with a set
      quantity: 1,
    };
  });

  pushEventToGTMDataLayer({
    event: 'view_item',
    is_a_set: items.length > 1,
    ecommerce: {
      items,
    },
    ...(setTitle && { set_title: setTitle }),
  });
};

export const fireGA4MenuLinkTracking = (link: HTMLAnchorElement) => {
  try {
    pushEventToGTMDataLayer({
      event: 'menu_link',
      link_id: link.dataset.nav,
      link_text: link.innerText,
      link_url: link.href,
    });
  } catch (err) {
    console.error(err);
  }
};

export const fireGA4LinkClickTracking = (link: HTMLAnchorElement) => {
  try {
    const {
      dataset: { linkId, linkText },
      href,
    } = link;
    pushEventToGTMDataLayer({
      event: 'link_click',
      link_id: linkId,
      link_text: linkText,
      link_url: href,
    });
  } catch (err) {
    console.error(err);
  }
};

export const getPageType = (path: string): string => {
  if (path !== '/') {
    path = path.replace(/\/$/, '');
  }

  const homepagePattern = /^\/([a-z]{2}-[a-z]{2})?$/;
  const collectionPattern = /\/collections\//;
  const productPattern = /\/products\//;
  const cartPattern = /\/cart/;

  switch (true) {
    case homepagePattern.test(path):
      return 'homepage';
    case collectionPattern.test(path):
      return 'plp';
    case productPattern.test(path):
      return 'pdp';
    case cartPattern.test(path):
      return 'cart';
    default:
      return 'other';
  }
};

export const fireGA4MenuClickEvent = (title: string, topCategory: string = '', subCategory: string = ''): void => {
  const pageType = getPageType(window.location.pathname);
  pushEventToGTMDataLayer({
    event: 'menu_click',
    top_category: topCategory || title,
    sub_category: subCategory || (topCategory && topCategory !== title ? title : ''),
    link_text: title,
    page_type: pageType,
  });
};

export const fireGA4PLPfilterTracking = (appliedFilters: [string, string][]) => {
  const keys = appliedFilters.map(([key, value]) => key);
  const values = appliedFilters.map(([key, value]) => value.replaceAll(',', '&'));
  pushEventToGTMDataLayer({
    event: 'filter_link',
    link_id: keys.join('&'),
    link_text: values.join('&'),
    link_url: window.location.href,
  });
};

export const fireGA4viewPLPproductsEvent = (
  products: ProductNode[],
  collectionName = '',
  startIndex: number,
  collectionShopifyId: string,
  merchant = ''
) => {
  clearPrevEcommerceObject();
  const items = products.map((product, index) => {
    const productTypeBreakdown = splitProductType(product.productType);
    return {
      item_name: product.title,
      item_id: getIdFromShopifyGraphQLId(product.id),
      item_brand: 'alo',
      affiliation: 'Alo_US',
      item_category: productTypeBreakdown[0] || '',
      item_category2: productTypeBreakdown[1] || '',
      item_category3: productTypeBreakdown[2] || '',
      item_category4: productTypeBreakdown[3] || '',
      item_variant: product.options.find((option) => option.name === 'Color')?.values.join(', '),
      item_list_name: collectionName,
      item_list_id: collectionShopifyId,
      index: index + 1 + startIndex,
      quantity: 1,
    };
  });

  pushEventToGTMDataLayer({
    event: 'view_item_list',
    merch_powered_by: merchant,
    ecommerce: {
      items,
    },
  });
};

export const fireGA4ClickOnProductFromListEvent = ({
  productType,
  productTitle,
  productId,
  productVariant,
  index,
  collectionShopifyId,
  collectionName = '',
  merchant,
}: {
  productType: string;
  productTitle: string;
  productId: string;
  productVariant: string | undefined;
  index: number;
  collectionShopifyId: string;
  collectionName: string | undefined;
  merchant?: string;
}) => {
  clearPrevEcommerceObject();
  const productTypeBreakdown = splitProductType(productType);
  const items = [
    {
      item_name: productTitle,
      item_id: productId,
      item_brand: 'alo',
      affiliation: 'Alo_US',
      item_category: productTypeBreakdown[0] || '',
      item_category2: productTypeBreakdown[1] || '',
      item_category3: productTypeBreakdown[2] || '',
      item_category4: productTypeBreakdown[3] || '',
      item_variant: productVariant,
      item_list_name: collectionName,
      item_list_id: collectionShopifyId,
      index: index + 1,
      quantity: 1,
    },
  ];

  pushEventToGTMDataLayer({
    event: 'select_item',
    merch_powered_by: merchant,
    ecommerce: {
      items,
    },
  });
};

export const fireGA4cartViewEvent = (cartItems: CartItem[]) => {
  clearPrevEcommerceObject();
  const items = cartItems.map((product) => {
    const productTypeBreakdown = splitProductType(product.product_type);
    return {
      item_name: product.product_title,
      item_id: product.product_id.toString(),
      item_brand: 'alo',
      affiliation: 'Alo_US',
      item_category: productTypeBreakdown[0] || '',
      item_category2: productTypeBreakdown[1] || '',
      item_category3: productTypeBreakdown[2] || '',
      item_category4: productTypeBreakdown[3] || '',
      item_variant: product.variant_title || '',
      index: '',
      quantity: product.quantity,
    };
  });

  pushEventToGTMDataLayer({
    event: 'view_cart',
    ecommerce: {
      items,
    },
  });
};

// function to send Find In Store events
export const fireFindInStoreDataLayerEvent = (
  eventName: string,
  sku: string | undefined,
  quantityAvailable: number | undefined,
  size: string | undefined,
  product: IColorwayProductData,
  latLong: LatLng
) => {
  pushEventToGTMDataLayer({
    event: eventName,
    product_title: product.title,
    color: product.colorName,
    size: size,
    sku: sku,
    customer_location: latLong,
    quantity_available_online: quantityAvailable,
  });
};

export const fireGA4CustomerAuthorizationEvent = (options: GA4CustomerAuthEventOptions) => {
  if (!options.event) return;
  pushEventToGTMDataLayer(options);

  // Clean up URL params to avoid duplicate events
  const urlParams = InitialUrlSearchParams;
  urlParams.delete('auth_success');
  urlParams.delete('is_modal');
  urlParams.delete('auth_event');
  urlParams.delete('social_login');
  const remainingURLparams = urlParams.toString().length > 0 ? urlParams.toString() : null;
  window.history.replaceState(
    {},
    document.title,
    window.location.pathname + `${remainingURLparams ? '?' + remainingURLparams : ''}`
  );
};

export const fireGA4AddOrRemoveWishlistEvent = (product: WishlistGA4ProductData, options: WishlistGA4Options) => {
  clearPrevEcommerceObject();
  const event = options.isWishlisted ? `remove_from_wishlist` : `add_to_wishlist`;
  const productTypeBreakdown = splitProductType(product.productType);
  const items = [
    {
      item_name: product.title || '',
      item_id: product.id || '',
      affiliation: 'Alo_US',
      item_brand: 'alo',
      ...(productTypeBreakdown[0] && { item_category: productTypeBreakdown[0] }),
      ...(productTypeBreakdown[1] && { item_category2: productTypeBreakdown[1] }),
      ...(productTypeBreakdown[2] && { item_category3: productTypeBreakdown[2] }),
      ...(productTypeBreakdown[3] && { item_category4: productTypeBreakdown[3] }),
      ...(product.variantTitle && { item_variant: product.variantTitle }),
      ...(product.source !== 'pdp' &&
        product.source !== 'cart' && {
          item_list_name: product?.collectionHandle,
          item_list_id: product?.collectionShopifyId,
          index: product?.index,
        }),
      quantity: 1,
    },
  ];
  pushEventToGTMDataLayer({
    event,
    ecommerce: {
      location: product.source,
      items,
    },
  });
};

interface GA4CustomerAuthEventOptions {
  event: 'login' | 'sign_up' | null;
  customer_id: string | null;
  loyalty_tier: string | null;
  method: string;
}

// Mobile Apps: Web-view GA4 events
const sendEventToGA4viaMeasurementProtocol = (data: GA4DataForMeasurmentProtocol) => {
  const normalizedUserAgent = window.navigator.userAgent.toLowerCase();
  const environment = normalizedUserAgent.includes('aloandroid')
    ? 'android'
    : normalizedUserAgent.includes('aloios')
    ? 'ios'
    : 'web';
  const firebase_app_id = process.env[`REACT_APP_GA4_${environment.toUpperCase()}_FIREBASE_APP_ID`];
  const api_secret = process.env[`REACT_APP_GA4_${environment.toUpperCase()}_API_SECRET`];

  if (!api_secret || !firebase_app_id) return;

  const url = `https://www.google-analytics.com/mp/collect?firebase_app_id=${firebase_app_id}&api_secret=${api_secret}`;
  const requestData = { ...data };
  // Remove locale subfolder from page_locations
  const requestDataWithoutLocale: GA4DataForMeasurmentProtocol = {
    ...requestData,
    events: requestData.events.map((event) => ({
      ...event,
      params: {
        ...event.params,
        page_location: event.params.page_location ? urlWithoutLocale(event.params.page_location) : undefined,
        site_subfolder: `${window?.Shopify?.locale?.toLowerCase()}-${window?.Shopify?.country?.toLowerCase()}`,
      },
    })),
  };

  return fetch(url, {
    method: 'POST',
    body: JSON.stringify(requestDataWithoutLocale),
  }).catch((error) => {
    console.error(error);
  });
};

const app_instance_id = getCookie('mobileAppInstanceId');
const session_id = getCookie('mobileSessionId');

export const fireGA4checkoutEventsForMobileApps = (eventObject?: GA4CheckoutEventData) => {
  const eventData = eventObject || window.ga4_event_data_for_mobile_apps;
  if (!eventData) return;

  const data: GA4DataForMeasurmentProtocol = {
    app_instance_id,
    events: [
      {
        name: eventData.event,
        params:
          eventData.event === 'purchase'
            ? {
                ...eventData.ecommerce,
                session_id,
                customer_segment: eventData.customer_segment,
                shipping_country: eventData.shipping_country,
              }
            : { ...eventData.ecommerce, session_id },
      },
    ],
  };
  sendEventToGA4viaMeasurementProtocol(data)?.then(() => {
    setCookie(NATIVE_APPLE_PAY_COOKIE_NAME, 'false', -1);
  });
};

export const fireGA4pageViewEventForMobileApps = () => {
  const page_location = window.location.href;

  const data: GA4DataForMeasurmentProtocol = {
    app_instance_id,
    events: [
      {
        name: 'page_view',
        params: { page_location, session_id },
      },
    ],
  };

  sendEventToGA4viaMeasurementProtocol(data);
};

interface GA4DataForMeasurmentProtocol {
  app_instance_id: string;
  events: {
    name: string;
    params: {
      session_id: string;
      items?: Item[];
      shipping_tier?: string;
      page_location?: string;
      transaction_id?: string;
      affiliation?: string;
      value?: number;
      tax?: number;
      shipping?: number;
      currency?: string;
      coupon?: string;
      debug_mode?: boolean;
      customer_segment?: 'domestic' | 'international';
      shipping_country?: string;
    };
  }[];
}

export enum BloomreachPageTypes {
  Homepage = 'homepage',
  PDP = 'product',
  PLP = 'category',
  SearchResult = 'search',
  Content = 'content',
  Thematic = 'thematic',
  Other = 'other',
}

export const pushBloomreachPageViewEventToDataLayer = (pageType: BloomreachPageTypes, additionalProps: object = {}) => {
  // Do not send mobile events to Bloomreach. They are sent from the mobile app.
  if (IS_NATIVE_APP_CLIENT) return;

  const initBrData = {
    ptype: pageType,
    title: document.title,
    user_id: Customer.id || '',
  };

  const mergedBrData = { ...initBrData, ...additionalProps };
  const brData = isProduction ? mergedBrData : { ...mergedBrData, test_data: true };

  // Set br_data that Bloomreach is expecting in GTM data layer
  window.dataLayer.push({ bloomreach_page_view_data: brData });

  // Trigger the Bloomreach page view event, which will read from data layer
  window.dataLayer.push({ event: 'bloomreach_page_view' });
};

export const pushBloomreachPageViewFromPathname = (pathname: string) => {
  // Bloomreach requires additional data for these pages.
  // The page view event will be pushed from the respective React components.
  // https://documentation.bloomreach.com/discovery/docs/global-page-view-pixel
  if (pathname.includes('/collections') || pathname.includes('/products') || pathname.includes('/search')) return;

  if (pathname === '/') {
    pushBloomreachPageViewEventToDataLayer(BloomreachPageTypes.Homepage);
  } else if (pathname.includes('/pages')) {
    pushBloomreachPageViewEventToDataLayer(BloomreachPageTypes.Content);
  } else {
    pushBloomreachPageViewEventToDataLayer(BloomreachPageTypes.Other);
  }
};

// Native Apple Pay Thank You Page utils
const NATIVE_APPLE_PAY_COOKIE_NAME = 'ApplePay';
export const checkIfNativeApplePayTYPage = () => getCookie(NATIVE_APPLE_PAY_COOKIE_NAME).toLowerCase() === 'true';

export const handleFormEvents = (eventName: string, mode?: string) => {
  const hasEventAlreadyFired = window.dataLayer.some((item: { event: string }) => item.event === eventName);

  if (!hasEventAlreadyFired) {
    pushEventToGTMDataLayer({
      event: eventName,
      ...(mode && { mode }),
    });
  }
};
